import {
  AsoScenarioAdditionalOneStepAdjustment,
  AsoScenarioStatus,
  AsoScenarioStoreTierType,
  AsoScenarioUseSets,
} from '@ydistri/api-sdk';
import type {
  AsoScenarioConfiguration,
  AsoScenarioScopeResponse,
  AsoScenarioStoreTierConfigurationResponse,
  AsoScenarioStoreTierResponse,
} from '@ydistri/api-sdk';
import type {
  AsoValidationArea,
  AsoValidationIssue,
  AsoValidationIssueCounts,
  AsoValidationIssueGroups,
  AsoValidationIssueKey,
  AsoValidationSeverity,
} from './asoValidationTypes';

export type AsoSettingsValidationInput = Pick<
  AsoScenarioConfiguration,
  | 'useSets'
  | 'additionalOneStepAdjustment'
  | 'supplyPreferenceModifier1Supply'
  | 'supplyPreferenceModifier1Modifier'
  | 'supplyPreferenceModifier2Supply'
  | 'supplyPreferenceModifier2Modifier'
  | 'supplyPreferenceModifier3Supply'
  | 'supplyPreferenceModifier3Modifier'
  | 'maxPickingPositionsForOneSku'
>;

export type AsoStoreTierConfigurationValidationInput = Pick<
  AsoScenarioStoreTierConfigurationResponse,
  | 'storeTier'
  | 'tierMultiplier'
  | 'balancingMinimumInventoryMonths'
  | 'balancingMedianInventoryMonths'
  | 'balancingMaximumInventoryMonths'
>;

export interface AsoStoreTierSettingsValidationInput {
  tier1Percent?: number | null;
  tier2Percent?: number | null;
}

export interface AsoStoreTierRecomputeStateValidationInput {
  /**
   * Pass [] after the tiers query has loaded with no rows.
   * null or undefined means the tier state is not known yet.
   */
  storeTiers?: readonly AsoScenarioStoreTierResponse[] | null;
  isStoreTierDataStale?: boolean;
  scenarioStatus?: AsoScenarioStatus | null;
}

export interface AsoValidationInput {
  configuration?: AsoSettingsValidationInput | null;
  scopes?: readonly AsoScenarioScopeResponse[] | null;
  storeTierConfigurations?: readonly AsoStoreTierConfigurationValidationInput[] | null;
  storeTierSettings?: AsoStoreTierSettingsValidationInput | null;
  storeTiers?: readonly AsoScenarioStoreTierResponse[] | null;
  isStoreTierDataStale?: boolean;
  scenarioStatus?: AsoScenarioStatus | null;
}

type HumanFeasibleSetRowIndex = 1 | 2 | 3;
type HumanFeasibleSetSupplyField = `supplyPreferenceModifier${HumanFeasibleSetRowIndex}Supply`;
type HumanFeasibleSetValueField = `supplyPreferenceModifier${HumanFeasibleSetRowIndex}Modifier`;
type StoreTierConfigurationField =
  | 'tierMultiplier'
  | 'balancingMinimumInventoryMonths'
  | 'balancingMedianInventoryMonths'
  | 'balancingMaximumInventoryMonths';

interface HumanFeasibleSetRow {
  rowNumber: HumanFeasibleSetRowIndex;
  supplyField: HumanFeasibleSetSupplyField;
  setValueField: HumanFeasibleSetValueField;
  supply: number | null | undefined;
  setValue: number | null | undefined;
}

interface StoreTierConfigurationFieldDefinition {
  field: StoreTierConfigurationField;
  label: string;
  kind: 'multiplier' | 'month';
}

type StoreTierValidationType =
  | AsoScenarioStoreTierType.Default
  | AsoScenarioStoreTierType.Tier1
  | AsoScenarioStoreTierType.Tier2
  | AsoScenarioStoreTierType.Tier3;

const asoValidationAreas: readonly AsoValidationArea[] = [
  'settings',
  'storeTiers',
  'scopes',
  'layers',
  'compute',
];

const storeTierConfigurationFields: readonly StoreTierConfigurationFieldDefinition[] = [
  { field: 'tierMultiplier', label: 'tier multiplier', kind: 'multiplier' },
  { field: 'balancingMinimumInventoryMonths', label: 'minimum inventory months', kind: 'month' },
  { field: 'balancingMedianInventoryMonths', label: 'median inventory months', kind: 'month' },
  { field: 'balancingMaximumInventoryMonths', label: 'maximum inventory months', kind: 'month' },
];

const requiredStoreTiers: readonly StoreTierValidationType[] = [
  AsoScenarioStoreTierType.Default,
  AsoScenarioStoreTierType.Tier1,
  AsoScenarioStoreTierType.Tier2,
  AsoScenarioStoreTierType.Tier3,
];

const configuredStoreTiers: readonly StoreTierValidationType[] = [
  AsoScenarioStoreTierType.Tier1,
  AsoScenarioStoreTierType.Tier2,
  AsoScenarioStoreTierType.Tier3,
];

export const minimumStoreTierMultiplier = 0.1;

const storeTierLabels: Record<StoreTierValidationType, string> = {
  [AsoScenarioStoreTierType.Default]: 'Default',
  [AsoScenarioStoreTierType.Tier1]: 'Tier 1',
  [AsoScenarioStoreTierType.Tier2]: 'Tier 2',
  [AsoScenarioStoreTierType.Tier3]: 'Tier 3',
};

const createIssue = (
  key: AsoValidationIssueKey,
  severity: AsoValidationSeverity,
  area: AsoValidationArea,
  message: string,
  details: Omit<AsoValidationIssue, 'key' | 'severity' | 'area' | 'message'> = {},
): AsoValidationIssue => ({
  key,
  severity,
  area,
  message,
  ...details,
});

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isPositiveInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isInteger(value) && value > 0;

const isNonNegativeInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isInteger(value) && value >= 0;

const shouldValidateHumanFeasibleSets = (useSets: AsoScenarioUseSets): boolean =>
  useSets === AsoScenarioUseSets.HumanFeasibleMinSets || useSets === AsoScenarioUseSets.Both;

const getHumanFeasibleSetRows = (
  configuration: AsoSettingsValidationInput,
): readonly [HumanFeasibleSetRow, HumanFeasibleSetRow, HumanFeasibleSetRow] => [
  {
    rowNumber: 1,
    supplyField: 'supplyPreferenceModifier1Supply',
    setValueField: 'supplyPreferenceModifier1Modifier',
    supply: configuration.supplyPreferenceModifier1Supply,
    setValue: configuration.supplyPreferenceModifier1Modifier,
  },
  {
    rowNumber: 2,
    supplyField: 'supplyPreferenceModifier2Supply',
    setValueField: 'supplyPreferenceModifier2Modifier',
    supply: configuration.supplyPreferenceModifier2Supply,
    setValue: configuration.supplyPreferenceModifier2Modifier,
  },
  {
    rowNumber: 3,
    supplyField: 'supplyPreferenceModifier3Supply',
    setValueField: 'supplyPreferenceModifier3Modifier',
    supply: configuration.supplyPreferenceModifier3Supply,
    setValue: configuration.supplyPreferenceModifier3Modifier,
  },
];

const hasAnyHumanFeasibleSetRowValue = (row: HumanFeasibleSetRow): boolean =>
  isFiniteNumber(row.supply) || isFiniteNumber(row.setValue);

const isHumanFeasibleSetRowComplete = (row: HumanFeasibleSetRow): boolean =>
  isFiniteNumber(row.supply) && isFiniteNumber(row.setValue);

const validateHumanFeasibleSetRowValues = (row: HumanFeasibleSetRow): AsoValidationIssue[] => {
  const issues: AsoValidationIssue[] = [];

  if (isFiniteNumber(row.supply) && !isPositiveInteger(row.supply)) {
    issues.push(
      createIssue(
        'humanFeasibleSetSupplyPositiveInteger',
        'error',
        'settings',
        `Human-feasible row ${row.rowNumber} supply threshold must be a positive integer.`,
        { field: row.supplyField },
      ),
    );
  }

  if (isFiniteNumber(row.setValue) && !isPositiveInteger(row.setValue)) {
    issues.push(
      createIssue(
        'humanFeasibleSetValuePositiveInteger',
        'error',
        'settings',
        `Human-feasible row ${row.rowNumber} set value must be a positive integer.`,
        { field: row.setValueField },
      ),
    );
  }

  return issues;
};

const validateHumanFeasibleSetRowOrder = (
  previousRow: HumanFeasibleSetRow,
  nextRow: HumanFeasibleSetRow,
): AsoValidationIssue[] => {
  const issues: AsoValidationIssue[] = [];

  if (
    isPositiveInteger(previousRow.supply) &&
    isPositiveInteger(nextRow.supply) &&
    nextRow.supply <= previousRow.supply
  ) {
    issues.push(
      createIssue(
        'humanFeasibleSetSupplyOrder',
        'error',
        'settings',
        `Human-feasible row ${nextRow.rowNumber} supply threshold must be greater than row ${previousRow.rowNumber}.`,
        { field: nextRow.supplyField },
      ),
    );
  }

  if (
    isPositiveInteger(previousRow.setValue) &&
    isPositiveInteger(nextRow.setValue) &&
    nextRow.setValue < previousRow.setValue
  ) {
    issues.push(
      createIssue(
        'humanFeasibleSetValueDecreases',
        'warning',
        'settings',
        `Human-feasible row ${nextRow.rowNumber} set value is lower than row ${previousRow.rowNumber}.`,
        { field: nextRow.setValueField },
      ),
    );
  }

  return issues;
};

export const hasForcedToZeroSource = (
  scopes?: readonly AsoScenarioScopeResponse[] | null,
): boolean => scopes?.some(scope => scope.source.isForcedToZero) ?? false;

export const validateHumanFeasibleSetRows = (
  configuration: AsoSettingsValidationInput,
): AsoValidationIssue[] => {
  if (!shouldValidateHumanFeasibleSets(configuration.useSets)) return [];

  const rows = getHumanFeasibleSetRows(configuration);
  const [row1, row2, row3] = rows;
  const issues: AsoValidationIssue[] = [];

  if (!isHumanFeasibleSetRowComplete(row1)) {
    issues.push(
      createIssue(
        'humanFeasibleSetRowRequired',
        'error',
        'settings',
        'Human-feasible row 1 requires both a supply threshold and a set value.',
        { field: row1.supplyField },
      ),
    );
  }

  [row2, row3].forEach(row => {
    if (hasAnyHumanFeasibleSetRowValue(row) && !isHumanFeasibleSetRowComplete(row)) {
      issues.push(
        createIssue(
          'humanFeasibleSetRowIncomplete',
          'error',
          'settings',
          `Human-feasible row ${row.rowNumber} must be fully blank or include both a supply threshold and a set value.`,
          { field: row.supplyField },
        ),
      );
    }
  });

  if (isHumanFeasibleSetRowComplete(row3) && !isHumanFeasibleSetRowComplete(row2)) {
    issues.push(
      createIssue(
        'humanFeasibleSetRowDependency',
        'error',
        'settings',
        'Human-feasible row 3 can only be used when row 2 is also filled.',
        { field: row3.supplyField },
      ),
    );
  }

  rows.forEach(row => {
    if (isHumanFeasibleSetRowComplete(row)) issues.push(...validateHumanFeasibleSetRowValues(row));
  });

  issues.push(...validateHumanFeasibleSetRowOrder(row1, row2));
  issues.push(...validateHumanFeasibleSetRowOrder(row2, row3));

  return issues;
};

export const validateMaxPickingPositions = (
  configuration: AsoSettingsValidationInput,
  scopes?: readonly AsoScenarioScopeResponse[] | null,
): AsoValidationIssue[] => {
  if (
    configuration.additionalOneStepAdjustment !==
    AsoScenarioAdditionalOneStepAdjustment.MaxPickingPositionsForOneSku
  ) {
    return [];
  }

  const issues: AsoValidationIssue[] = [];

  if (!isFiniteNumber(configuration.maxPickingPositionsForOneSku)) {
    issues.push(
      createIssue(
        'maxPickingPositionsRequired',
        'error',
        'settings',
        'Max picking positions for one SKU is required when the adjustment is enabled.',
        { field: 'maxPickingPositionsForOneSku' },
      ),
    );
  } else if (!isPositiveInteger(configuration.maxPickingPositionsForOneSku)) {
    issues.push(
      createIssue(
        'maxPickingPositionsPositiveInteger',
        'error',
        'settings',
        'Max picking positions for one SKU must be a positive integer.',
        { field: 'maxPickingPositionsForOneSku' },
      ),
    );
  }

  if ((scopes?.length ?? 0) > 0 && !hasForcedToZeroSource(scopes)) {
    issues.push(
      createIssue(
        'maxPickingPositionsWithoutForcedToZeroSource',
        'warning',
        'settings',
        'Max picking positions has no practical effect unless at least one source scope is forced to zero.',
        { field: 'maxPickingPositionsForOneSku' },
      ),
    );
  }

  return issues;
};

export const validateUseSetAsMaxQuantity = (
  configuration: AsoSettingsValidationInput,
): AsoValidationIssue[] => {
  if (
    configuration.additionalOneStepAdjustment !==
      AsoScenarioAdditionalOneStepAdjustment.UseSetAsMaxQuantityForOneStep ||
    configuration.useSets !== AsoScenarioUseSets.DontUse
  ) {
    return [];
  }

  return [
    createIssue(
      'useSetAsMaxQuantityWithoutSets',
      'warning',
      'settings',
      'Use set as max quantity is enabled, but sets are disabled; one-step transfer quantities may stay capped at one unit.',
      { field: 'additionalOneStepAdjustment' },
    ),
  ];
};

export const validateAsoSettings = (
  configuration?: AsoSettingsValidationInput | null,
  scopes?: readonly AsoScenarioScopeResponse[] | null,
): AsoValidationIssue[] => {
  if (!configuration) return [];

  return [
    ...validateHumanFeasibleSetRows(configuration),
    ...validateMaxPickingPositions(configuration, scopes),
    ...validateUseSetAsMaxQuantity(configuration),
  ];
};

const normalizeStoreTier = (storeTier?: AsoScenarioStoreTierType | null): StoreTierValidationType =>
  storeTier ?? AsoScenarioStoreTierType.Default;

const createStoreTierConfigurationMap = (
  rows: readonly AsoStoreTierConfigurationValidationInput[],
): ReadonlyMap<StoreTierValidationType, AsoStoreTierConfigurationValidationInput> => {
  const rowByTier = new Map<StoreTierValidationType, AsoStoreTierConfigurationValidationInput>();

  rows.forEach(row => {
    const storeTier = normalizeStoreTier(row.storeTier);

    if (!rowByTier.has(storeTier)) rowByTier.set(storeTier, row);
  });

  return rowByTier;
};

const getStoreTierFieldValue = (
  row: AsoStoreTierConfigurationValidationInput | undefined,
  field: StoreTierConfigurationField,
): number | null | undefined => row?.[field];

const getEffectiveStoreTierValue = (
  tierRow: AsoStoreTierConfigurationValidationInput | undefined,
  defaultRow: AsoStoreTierConfigurationValidationInput | undefined,
  field: StoreTierConfigurationField,
): number | null | undefined => {
  const tierValue = getStoreTierFieldValue(tierRow, field);

  return isFiniteNumber(tierValue) ? tierValue : getStoreTierFieldValue(defaultRow, field);
};

const validateStoreTierEffectiveField = (
  tier: StoreTierValidationType,
  fieldDefinition: StoreTierConfigurationFieldDefinition,
  value: number | null | undefined,
): AsoValidationIssue[] => {
  if (!isFiniteNumber(value)) {
    return [
      createIssue(
        'storeTierEffectiveValueMissing',
        'error',
        'storeTiers',
        `${storeTierLabels[tier]} is missing an effective ${fieldDefinition.label} value.`,
        { field: fieldDefinition.field, storeTier: tier },
      ),
    ];
  }

  if (fieldDefinition.kind === 'multiplier' && value < minimumStoreTierMultiplier) {
    return [
      createIssue(
        'storeTierMultiplierPositive',
        'error',
        'storeTiers',
        `${storeTierLabels[tier]} tier multiplier must be at least ${minimumStoreTierMultiplier}.`,
        { field: fieldDefinition.field, storeTier: tier },
      ),
    ];
  }

  if (fieldDefinition.kind === 'month' && !isNonNegativeInteger(value)) {
    return [
      createIssue(
        'storeTierMonthNonNegativeInteger',
        'error',
        'storeTiers',
        `${storeTierLabels[tier]} ${fieldDefinition.label} must be a non-negative integer.`,
        { field: fieldDefinition.field, storeTier: tier },
      ),
    ];
  }

  return [];
};

const validateStoreTierMonthOrder = (
  tier: StoreTierValidationType,
  defaultRow: AsoStoreTierConfigurationValidationInput | undefined,
  tierRow: AsoStoreTierConfigurationValidationInput | undefined,
): AsoValidationIssue[] => {
  const minimum = getEffectiveStoreTierValue(
    tierRow,
    defaultRow,
    'balancingMinimumInventoryMonths',
  );
  const median = getEffectiveStoreTierValue(tierRow, defaultRow, 'balancingMedianInventoryMonths');
  const maximum = getEffectiveStoreTierValue(
    tierRow,
    defaultRow,
    'balancingMaximumInventoryMonths',
  );

  if (!isFiniteNumber(minimum) || !isFiniteNumber(median) || !isFiniteNumber(maximum)) return [];
  if (minimum <= median && median <= maximum) return [];

  return [
    createIssue(
      'storeTierMonthOrder',
      'error',
      'storeTiers',
      `${storeTierLabels[tier]} balancing months must be ordered as minimum <= median <= maximum.`,
      { field: 'balancingMedianInventoryMonths', storeTier: tier },
    ),
  ];
};

export const validateAsoStoreTierConfigurations = (
  rows?: readonly AsoStoreTierConfigurationValidationInput[] | null,
): AsoValidationIssue[] => {
  if (!rows) return [];

  const rowByTier = createStoreTierConfigurationMap(rows);
  const defaultRow = rowByTier.get(AsoScenarioStoreTierType.Default);
  const issues: AsoValidationIssue[] = [];

  requiredStoreTiers.forEach(tier => {
    if (!rowByTier.has(tier)) {
      issues.push(
        createIssue(
          'storeTierConfigurationRowMissing',
          'error',
          'storeTiers',
          `${storeTierLabels[tier]} store-tier configuration row is missing.`,
          { storeTier: tier },
        ),
      );
    }
  });

  configuredStoreTiers.forEach(tier => {
    const tierRow = rowByTier.get(tier);

    if (!tierRow) return;

    storeTierConfigurationFields.forEach(fieldDefinition => {
      const value = getEffectiveStoreTierValue(tierRow, defaultRow, fieldDefinition.field);
      issues.push(...validateStoreTierEffectiveField(tier, fieldDefinition, value));
    });

    issues.push(...validateStoreTierMonthOrder(tier, defaultRow, tierRow));
  });

  return issues;
};

const validateStoreTierPercentage = (
  value: number | null | undefined,
  field: 'tier1Percent' | 'tier2Percent',
  label: string,
): AsoValidationIssue[] => {
  if (!isFiniteNumber(value)) {
    return [
      createIssue(
        'storeTierPercentRequired',
        'error',
        'storeTiers',
        `${label} percentage is required.`,
        { field },
      ),
    ];
  }

  const issues: AsoValidationIssue[] = [];

  if (value < 0 || value > 100) {
    return [
      createIssue(
        'storeTierPercentRange',
        'error',
        'storeTiers',
        `${label} percentage must be between 0 and 100.`,
        {
          field,
        },
      ),
    ];
  }

  if (!Number.isInteger(value)) {
    issues.push(
      createIssue(
        'storeTierPercentInteger',
        'error',
        'storeTiers',
        `${label} percentage must be an integer.`,
        {
          field,
        },
      ),
    );
  }

  return issues;
};

export const validateAsoStoreTierSettings = (
  settings?: AsoStoreTierSettingsValidationInput | null,
): AsoValidationIssue[] => {
  if (!settings) return [];

  const issues = [
    ...validateStoreTierPercentage(settings.tier1Percent, 'tier1Percent', 'Tier 1'),
    ...validateStoreTierPercentage(settings.tier2Percent, 'tier2Percent', 'Tier 2'),
  ];

  if (isFiniteNumber(settings.tier1Percent) && isFiniteNumber(settings.tier2Percent)) {
    const total = settings.tier1Percent + settings.tier2Percent;

    if (total > 100) {
      issues.push(
        createIssue(
          'storeTierPercentTotal',
          'error',
          'storeTiers',
          'Tier 1 and Tier 2 percentages must add up to 100 or less.',
          { field: 'tier2Percent' },
        ),
      );
    }
  }

  return issues;
};

const validateAsoScopeSource = (scope: AsoScenarioScopeResponse): AsoValidationIssue[] => {
  const { source } = scope;
  if (source.scopeId !== null && source.scopeId !== undefined) return [];

  return [
    createIssue(
      'scopeSourceMissing',
      'error',
      'scopes',
      'Source must have a reusable Scope selected.',
      { field: 'scopeId', scopeId: scope.id, sourceId: source.id },
    ),
  ];
};

const validateAsoScopeTarget = (
  scope: AsoScenarioScopeResponse,
  target: AsoScenarioScopeResponse['targets'][number],
): AsoValidationIssue[] => {
  if (target.scopeId !== null && target.scopeId !== undefined) return [];

  return [
    createIssue(
      'scopeTargetScopeMissing',
      'error',
      'scopes',
      'Target must have a reusable Scope selected.',
      { field: 'scopeId', scopeId: scope.id, targetId: target.id },
    ),
  ];
};

export const validateAsoScopes = (
  scopes?: readonly AsoScenarioScopeResponse[] | null,
): AsoValidationIssue[] => {
  if (!scopes) return [];

  if (scopes.length === 0) {
    return [
      createIssue(
        'scopeMissing',
        'error',
        'scopes',
        'Scenario must include at least one ASO scope.',
      ),
    ];
  }

  const issues: AsoValidationIssue[] = [];

  scopes.forEach(scope => {
    issues.push(...validateAsoScopeSource(scope));

    if (scope.targets.length === 0) {
      issues.push(
        createIssue(
          'scopeTargetMissing',
          'error',
          'scopes',
          'Scope must include at least one target.',
          { scopeId: scope.id },
        ),
      );
    }

    scope.targets.forEach(target => {
      issues.push(...validateAsoScopeTarget(scope, target));
    });
  });

  return issues;
};

export const validateAsoStoreTierRecomputeState = (
  input: AsoStoreTierRecomputeStateValidationInput = {},
): AsoValidationIssue[] => {
  const issues: AsoValidationIssue[] = [];

  if (input.storeTiers?.length === 0) {
    issues.push(
      createIssue(
        'storeTiersMissing',
        'warning',
        'storeTiers',
        'Store tiers have not been computed yet; tier-specific configuration may not apply as expected.',
      ),
    );
  }

  if (input.isStoreTierDataStale === true) {
    issues.push(
      createIssue(
        'storeTiersStale',
        'warning',
        'storeTiers',
        'Scenario data is marked outdated; recompute store tiers after major scope changes when tier-specific behavior matters.',
      ),
    );
  }

  return issues;
};

export const validateAso = (input: AsoValidationInput): AsoValidationIssue[] => [
  ...validateAsoSettings(input.configuration, input.scopes),
  ...validateAsoStoreTierConfigurations(input.storeTierConfigurations),
  ...validateAsoStoreTierSettings(input.storeTierSettings),
  ...validateAsoScopes(input.scopes),
  ...validateAsoStoreTierRecomputeState({
    storeTiers: input.storeTiers,
    isStoreTierDataStale: input.isStoreTierDataStale,
    scenarioStatus: input.scenarioStatus,
  }),
];

export const getAsoValidationIssuesForArea = (
  issues: readonly AsoValidationIssue[],
  area: AsoValidationArea,
): AsoValidationIssue[] => issues.filter(issue => issue.area === area);

export const getAsoValidationIssuesForField = (
  issues: readonly AsoValidationIssue[],
  field: string,
): AsoValidationIssue[] => issues.filter(issue => issue.field === field);

export const getAsoValidationIssueStatus = (
  issues: readonly AsoValidationIssue[],
): AsoValidationSeverity | undefined => {
  if (issues.some(issue => issue.severity === 'error')) return 'error';
  if (issues.some(issue => issue.severity === 'warning')) return 'warning';

  return undefined;
};

export const getAsoValidationIssuesForStoreTier = (
  issues: readonly AsoValidationIssue[],
  storeTier?: AsoScenarioStoreTierType | null,
): AsoValidationIssue[] => {
  const normalizedStoreTier = normalizeStoreTier(storeTier);

  return issues.filter(issue => issue.storeTier === normalizedStoreTier);
};

export const getAsoValidationIssuesForStoreTierField = (
  issues: readonly AsoValidationIssue[],
  storeTier: AsoScenarioStoreTierType | null | undefined,
  field: string,
): AsoValidationIssue[] =>
  getAsoValidationIssuesForStoreTier(issues, storeTier).filter(issue => issue.field === field);

export const getAsoValidationIssuesForScopeList = (
  issues: readonly AsoValidationIssue[],
): AsoValidationIssue[] => issues.filter(issue => issue.key === 'scopeMissing');

export const getAsoValidationIssuesForScope = (
  issues: readonly AsoValidationIssue[],
  scopeId: number,
): AsoValidationIssue[] => issues.filter(issue => issue.scopeId === scopeId);

export const getAsoValidationIssuesForScopeCard = (
  issues: readonly AsoValidationIssue[],
  scopeId: number,
): AsoValidationIssue[] =>
  getAsoValidationIssuesForScope(issues, scopeId).filter(
    issue => issue.sourceId === undefined && issue.targetId === undefined,
  );

export const getAsoValidationIssuesForScopeSource = (
  issues: readonly AsoValidationIssue[],
  scopeId: number,
  sourceId: number,
): AsoValidationIssue[] =>
  getAsoValidationIssuesForScope(issues, scopeId).filter(issue => issue.sourceId === sourceId);

export const getAsoValidationIssuesForScopeTarget = (
  issues: readonly AsoValidationIssue[],
  scopeId: number,
  targetId: number,
): AsoValidationIssue[] =>
  getAsoValidationIssuesForScope(issues, scopeId).filter(issue => issue.targetId === targetId);

export const isAsoStoreTierRecomputeStateIssue = (issue: AsoValidationIssue): boolean =>
  issue.key === 'storeTiersMissing' || issue.key === 'storeTiersStale';

export const filterAsoValidationIssuesBySeverity = (
  issues: readonly AsoValidationIssue[],
  severity: AsoValidationSeverity,
): AsoValidationIssue[] => issues.filter(issue => issue.severity === severity);

export const getAsoValidationErrors = (
  issues: readonly AsoValidationIssue[],
): AsoValidationIssue[] => filterAsoValidationIssuesBySeverity(issues, 'error');

export const getAsoValidationWarnings = (
  issues: readonly AsoValidationIssue[],
): AsoValidationIssue[] => filterAsoValidationIssuesBySeverity(issues, 'warning');

export const hasAsoValidationErrors = (issues: readonly AsoValidationIssue[]): boolean =>
  issues.some(issue => issue.severity === 'error');

export const getAsoValidationIssueCounts = (
  issues: readonly AsoValidationIssue[],
): AsoValidationIssueCounts => ({
  errors: getAsoValidationErrors(issues).length,
  warnings: getAsoValidationWarnings(issues).length,
});

export const createEmptyAsoValidationIssueGroups = (): AsoValidationIssueGroups => ({
  settings: [],
  storeTiers: [],
  scopes: [],
  layers: [],
  compute: [],
});

export const getAsoValidationIssuesByArea = (
  issues: readonly AsoValidationIssue[],
): AsoValidationIssueGroups =>
  issues.reduce<AsoValidationIssueGroups>((groups, issue) => {
    groups[issue.area].push(issue);

    return groups;
  }, createEmptyAsoValidationIssueGroups());

export const getAsoValidationAreas = (): readonly AsoValidationArea[] => asoValidationAreas;
